import { useState, useRef, useEffect } from "react";
import { ChevronRight, ShoppingCart, Eye } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useFlyToCart } from "@/hooks/useFlyToCart";
import QuickViewDialog from "@/components/QuickViewDialog";
import { formatPrice } from "@/components/ui/types";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ProductApi } from "@/api/api";
import { buildImageUrl } from "@/lib/image";

interface FeaturedProduct {
  id: number;
  title: string;
  image: string;
  category: string;
  category_label: string;
  description: string;
  detailedDescription?: string;
  price: number;
  unit: string;
  productQuantityId: number;
  discount?: number;
  originalPrice?: number;
}

// Mở rộng interface Props để nhận thêm filterQuery động và link điều hướng tùy biến
interface FeaturedProductsProps {
  header: string;
  filterQuery?: Record<string, any>; // Prop nhận object filter linh hoạt (VD: { isfeatured: 1 } hoặc { category_id: 2 })
  viewAllLink?: string;              // Prop tùy chọn đường dẫn khi bấm "Xem tất cả"
}

const FeaturedProducts = ({ 
  header, 
  filterQuery = { isfeatured: 1 }, // Giá trị mặc định nếu không truyền là lấy sản phẩm nổi bật
  viewAllLink = "/san-pham/combo-da-khuech-tan-tinh-dau" 
}: FeaturedProductsProps) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const [quickViewProduct, setQuickViewProduct] = useState<FeaturedProduct | null>(null);
  const [featuredList, setFeaturedList] = useState<FeaturedProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { addToCart } = useCart();
  const { flyToCart } = useFlyToCart();
  const imgRefs = useRef<Record<number, HTMLImageElement | null>>({});

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        setIsLoading(true);
        // Khởi chạy API với filterQuery động truyền vào từ Prop
        const response = await ProductApi.getAll({ 
          pageSize: 4, 
          filters: JSON.stringify(filterQuery) 
        });
        console.log(response);

        const rawProducts = response.data?.items || [];

        const mapped: FeaturedProduct[] = rawProducts.map((p: any) => {
          let productPrice = 0;
          let productUnit = "Sản phẩm";
          let productQuantityId = 0;

          if (p.product_quantities && p.product_quantities.length > 0) {
            const firstItem = p.product_quantities[0];
            if (firstItem.price) productPrice = Number(firstItem.price);
            if (firstItem.quantities?.name) productUnit = firstItem.quantities.name;
            if (firstItem.id) productQuantityId = firstItem.id;
          }

          return {
            id: p.id,
            title: p.name,
            price: productPrice,
            image: buildImageUrl(p.image_link),
            unit: productUnit,
            category: String(p.category_id),
            category_label: p.categories?.name || "Sản phẩm",
            description: p.short_description || "",
            detailedDescription: p.detailed_description || "",
            productQuantityId,
          };
        });

        setFeaturedList(mapped);
      } catch (error) {
        console.error("Lỗi khi fetch featured products:", error);
        setFeaturedList([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedProducts();
    // Thêm filterQuery vào dependency array để cập nhật lại danh sách khi prop thay đổi
  }, [JSON.stringify(filterQuery)]); 

  const handleAddToCart = (p: FeaturedProduct) => {
    addToCart({ id: p.id, title: p.title, price: p.price, image: p.image, unit: p.unit }, 1, p.productQuantityId);
    toast.success(`Đã thêm "${p.title}" vào giỏ hàng`);
    const imgEl = imgRefs.current[p.id];
    if (imgEl) flyToCart(p.image, imgEl);
  };

  if (isLoading) {
    return (
      <section id="products" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-heading text-center text-foreground mb-4">
            {header}
          </h2>
          <div className="w-16 h-0.5 bg-accent mx-auto mb-12" />
          <div className="text-center text-muted-foreground">Đang tải...</div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="products" className="py-12 md:py-20 bg-muted/30 mb-6 md:mb-10">
        <div className="container mx-auto px-4">
          {/* Thay đổi từ chữ viết cứng thành biến {header} động */}
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-heading text-center text-foreground mb-4">
            {header}
          </h2>
          <div className="w-16 h-0.5 bg-accent mx-auto mb-12" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredList.map((p, i) => (
              <div
                key={p.id}
                className="group bg-card rounded-sm overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className="relative overflow-hidden aspect-square">
                  <img
                    ref={el => { imgRefs.current[p.id] = el; }}
                    src={p.image}
                    srcSet={`
                      ${p.image}?w=300 300w,
                      ${p.image}?w=600 600w,
                      ${p.image}?w=900 900w
                    `}
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    width="662"
                    height="662"
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading={i < 4 ? "eager" : "lazy"}
                  />
                  {p.discount && p.discount > 0 && (
                    <span className="absolute top-3 right-3 bg-discount text-primary-foreground text-xs font-semibold px-2 py-1 rounded-sm">
                      -{p.discount}%
                    </span>
                  )}
                  <div
                    className={`absolute inset-0 bg-foreground/40 flex items-center justify-center gap-3 transition-opacity duration-300 ${
                      hovered === i ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <button
                      onClick={() => handleAddToCart(p)}
                      className="w-10 h-10 bg-background text-foreground rounded-full flex items-center justify-center hover:bg-muted transition-colors"
                      aria-label="Add to cart"
                    >
                      <ShoppingCart size={18} />
                    </button>
                    <button
                      onClick={() => setQuickViewProduct(p)}
                      className="w-10 h-10 bg-background text-foreground rounded-full flex items-center justify-center hover:bg-muted transition-colors"
                      aria-label="Quick view"
                    >
                      <Eye size={18} />
                    </button>
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="text-lg md:text-2xl font-heading font-bold text-foreground mt-1 line-clamp-2 min-h-[1rem]">
                    {p.title}
                  </h3>
                  <span className="text-[12px] text-muted-foreground mb-2">
                    {p.category_label}
                  </span>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-price font-semibold">
                      {formatPrice(p.price)}
                      <span className="text-muted-foreground font-normal ml-1 text-xs">/ {p.unit}</span>
                    </span>
                    {p.originalPrice && p.originalPrice > p.price && (
                      <span className="text-muted-foreground text-sm line-through">
                        {formatPrice(p.originalPrice)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to={viewAllLink}
              className="inline-flex items-center gap-2 text-sm text-foreground tracking-wider hover:text-primary-cyan transition-colors uppercase"
            >
              Xem tất cả <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {quickViewProduct && (
        <QuickViewDialog
          product={{
            id: quickViewProduct.id,
            image: quickViewProduct.image,
            title: quickViewProduct.title,
            category: quickViewProduct.category,
            category_label: quickViewProduct.category_label,
            description: quickViewProduct.description,
          }}
          open={!!quickViewProduct}
          onOpenChange={(open) => !open && setQuickViewProduct(null)}
        />
      )}
    </>
  );
};

export default FeaturedProducts;